import { useEffect, useState } from 'react';
import { useAllOwnedProducts, useCurrencyAndPurchaseOptions, useIsPublicUseCase, useOwnedHubTierWithinSkuCategory, useProducts, usePurchaseMotions } from '../data-cache/dataCacheHooks';
import { tryAndGetEligiblePurchaseOptionByTerm } from '../data-cache/fetchPurchaseOptions';
import { useFeatureTableSessionCache } from '../FeatureTableSessionCacheProvider';
import { getProductLevelIndex, isSeatBasedSkuCategoryName, sortByProductLevel } from './tableHeaderConfig';
const createTableHeaderConfigInitialState = (productApiNames, skuCategoryName) => {
  return productApiNames.sort(sortByProductLevel).map(productApiName => {
    return {
      productApiName,
      isPortalsCurrentSubscription: undefined,
      isSeatBasedProduct: isSeatBasedSkuCategoryName(skuCategoryName),
      isTierBelowCurrentSubscription: undefined,
      price: undefined,
      purchaseMotion: undefined
    };
  });
};
export const useTableHeaderConfig = () => {
  const isPublicUseCase = useIsPublicUseCase();
  const {
    productApiNames,
    skuCategoryName,
    sessionCacheKey,
    term
  } = useFeatureTableSessionCache();
  const [tableHeaderConfig, setTableHeaderConfig] = useState({
    hasDerivedCurrentSubscription: false,
    configs: createTableHeaderConfigInitialState(productApiNames, skuCategoryName)
  });
  const allOwnedProducts = useAllOwnedProducts();
  const ownedHubTierWithinSkuCategory = useOwnedHubTierWithinSkuCategory();
  const products = useProducts();
  const areProductDetailsFetchedAndValid = products && products.length > 0;
  const currencyAndPurchaseOptions = useCurrencyAndPurchaseOptions();
  const purchaseMotionsByProductApiName = usePurchaseMotions();
  useEffect(() => {
    setTableHeaderConfig(prevTableHeaderConfig => {
      // Safe guard against consumer unexpectedly re-deriving and resetting the
      // same productApiNames prop values which will disrupt the current plan
      // badge derivation
      const prevProductApiNames = prevTableHeaderConfig.configs.map(config => config.productApiName);
      if (productApiNames.every((productApiName, index) => productApiName === prevProductApiNames[index])) {
        return prevTableHeaderConfig;
      } else {
        return {
          hasDerivedCurrentSubscription: false,
          configs: createTableHeaderConfigInitialState(productApiNames, skuCategoryName)
        };
      }
    });
  }, [productApiNames, skuCategoryName]);
  useEffect(() => {
    if (areProductDetailsFetchedAndValid && allOwnedProducts !== undefined && allOwnedProducts !== null) {
      const currentSubscriptionProductApiName = ownedHubTierWithinSkuCategory || 'free';
      setTableHeaderConfig(prevTableHeaderConfig => ({
        hasDerivedCurrentSubscription: true,
        configs: prevTableHeaderConfig.configs.map(productColumn => {
          return Object.assign({}, productColumn, {
            isPortalsCurrentSubscription: productColumn.productApiName === currentSubscriptionProductApiName || productColumn.productApiName.includes('free') && currentSubscriptionProductApiName === 'free',
            isTierBelowCurrentSubscription: !currentSubscriptionProductApiName ? false : getProductLevelIndex(productColumn.productApiName) < getProductLevelIndex(currentSubscriptionProductApiName)
          });
        })
      }));
    }
  }, [allOwnedProducts, areProductDetailsFetchedAndValid, ownedHubTierWithinSkuCategory, sessionCacheKey]);
  useEffect(() => {
    if (areProductDetailsFetchedAndValid && purchaseMotionsByProductApiName && allOwnedProducts !== undefined && allOwnedProducts !== null && tableHeaderConfig.hasDerivedCurrentSubscription) {
      setTableHeaderConfig(prevTableHeaderConfig => Object.assign({}, prevTableHeaderConfig, {
        configs: prevTableHeaderConfig.configs.map(productColumn => {
          const purchaseMotionForCurrentProduct = purchaseMotionsByProductApiName === null || purchaseMotionsByProductApiName === void 0 ? void 0 : purchaseMotionsByProductApiName[productColumn.productApiName];
          const ownsFreeTierOfProductLine =
          // Free tier apiNames are unique to Monetization services and will
          // not be returned by product inventory
          !!(productColumn.isPortalsCurrentSubscription && (productColumn.productApiName.includes('free') || purchaseMotionForCurrentProduct === undefined));
          const showCurrentPlanButton = !isPublicUseCase && (ownsFreeTierOfProductLine || productColumn.isPortalsCurrentSubscription && !purchaseMotionForCurrentProduct.toString().includes('SEAT'));
          const displayPurchaseMotion = purchaseMotionForCurrentProduct && !productColumn.isTierBelowCurrentSubscription;
          return Object.assign({}, productColumn, {
            purchaseMotion: showCurrentPlanButton ? 'CURRENT_PLAN' : displayPurchaseMotion ? purchaseMotionForCurrentProduct : null
          });
        })
      }));
    }
  }, [allOwnedProducts, areProductDetailsFetchedAndValid, isPublicUseCase, ownedHubTierWithinSkuCategory, purchaseMotionsByProductApiName, sessionCacheKey, tableHeaderConfig.hasDerivedCurrentSubscription]);
  useEffect(() => {
    if (areProductDetailsFetchedAndValid && currencyAndPurchaseOptions) {
      setTableHeaderConfig(prevTableHeaderConfig => Object.assign({}, prevTableHeaderConfig, {
        configs: prevTableHeaderConfig.configs.map(productColumn => {
          var _currencyAndPurchaseO, _currencyAndPurchaseO2;
          const eligiblePurchaseOptions = (_currencyAndPurchaseO = currencyAndPurchaseOptions[productColumn.productApiName]) === null || _currencyAndPurchaseO === void 0 ? void 0 : _currencyAndPurchaseO.purchaseOptions;
          const purchaseOption = eligiblePurchaseOptions ? tryAndGetEligiblePurchaseOptionByTerm(eligiblePurchaseOptions, term) : undefined;
          return Object.assign({}, productColumn, {
            price: Object.assign({}, productColumn.price, {
              // Default price to 0, which will cover the case when the product tier is
              // free or a portal is ineligible to purchase the product. In both cases,
              // the price will be hidden in the table
              price: Number((purchaseOption === null || purchaseOption === void 0 ? void 0 : purchaseOption.monthlyPrice) || 0),
              currencyCode: ((_currencyAndPurchaseO2 = currencyAndPurchaseOptions[productColumn.productApiName]) === null || _currencyAndPurchaseO2 === void 0 ? void 0 : _currencyAndPurchaseO2.currencyCode) || 'USD'
            })
          });
        })
      }));
    }
  }, [areProductDetailsFetchedAndValid, currencyAndPurchaseOptions, term]);
  return tableHeaderConfig.configs;
};